"use client";

import { cn } from "@/lib/utils";
import { pushGtmEvent } from "@/utils/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

/**
 * NavBar 1047 — BDU main nav (bduedu.in `organisms/Header`).
 *
 * White bar: logo left, nav links right, gradient "Apply" CTA. Its distinguishing
 * feature is a THREE-level menu — a top-level link (Programmes) opens a dropdown
 * of programmes, and a programme with specialisations expands them inline rather
 * than navigating. Every other navbar variant here stops at two levels.
 *
 * v1 ships three near-duplicate DOM blocks (≥1024, 860–1023, <860). The first two
 * differ only in bar height, logo size and padding, so they are one responsive
 * block here; the hamburger bar under 860px stays separate as in v1.
 *
 * Breakpoints are literal pixels from v1 (`lg` = 1024, `min-[860px]`), so they are
 * written as arbitrary variants — this app's `sm`/`md`/`lg` are remapped to
 * 1080/1230/1430 and would silently move both cut-offs.
 */

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  icon_alt?: string;
  text_color?: string;
  children?: MenuItem[];
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  textColor?: string;
  icon?: string;
  iconAlt?: string;
  children: RouteItem[];
}

/* ------------------------------------------------------------------ */
/* Chrome baked into the reference (not CMS fields)                     */
/* ------------------------------------------------------------------ */

/** `bg-gray-300` — the tint v1 puts behind an open trigger / current item. */
const HIGHLIGHT_BG = "#EAECF0";
/** `text-gray-600` — third-level (specialisation) label colour. */
const SUBITEM_TEXT = "#475467";
/**
 * The rule under each expandable row of the mobile drawer. v1 gets it from
 * shadcn's `AccordionItem`, which hardcodes `border-b border-[#EFEFEF]` — a
 * lighter line than the theme border the cards use, and only the expandable
 * rows (Programmes / Student Corner / Admission) are accordion items, so the
 * plain links between them are NOT separated.
 */
const ACCORDION_DIVIDER = "#EFEFEF";
/** v1's active-link gradient geometry (`bg-header-gradient`). */
const ACTIVE_GRADIENT_ANGLE = "94.73deg";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

const isExternal = (href: string) =>
  /^https?:\/\//i.test(href) ||
  href.startsWith("//") ||
  href.startsWith("tel:") ||
  href.startsWith("mailto:");

/**
 * next/image throws on any absolute src whose host is missing from
 * `next.config.mjs`'s `hosts` whitelist, which would take the whole page (and the
 * admin page builder) down over one CMS value. That list only carries the per-tenant
 * asset buckets, so an absolute URL from anywhere else bypasses the optimizer instead
 * — same guard Header 1046 / Header 7 use. Bucket-hosted uploads stay optimized.
 */
function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

/** Two-stop gradient when a second colour is set, otherwise the flat first stop. */
function accentBackground(from: string, to: string, angle: string): string {
  return to && to !== from
    ? `linear-gradient(${angle}, ${from}, ${to})`
    : from;
}

/** Backend menu tree → routes, active-only and priority-sorted, at any depth. */
function toRoutes(items: MenuItem[] | undefined): RouteItem[] {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items
    .filter((item) => item.status)
    .sort((a, b) => a.priority - b.priority)
    .map((item) => ({
      id: item.id,
      route: item.url && item.url.trim() !== "" ? item.url : `/${item.slug}`,
      label: item.title,
      textColor: item.text_color,
      icon: item.icon,
      iconAlt: item.icon_alt,
      children: toRoutes(item.children),
    }));
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const ChevronDown = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden
    className={cn(
      "ml-1 shrink-0 transition-transform duration-300 ease-in-out",
      open ? "rotate-180" : "rotate-0",
    )}
  >
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = ({ open, color }: { open: boolean; color: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden
    className={cn(
      "shrink-0 transition-transform duration-300",
      open ? "rotate-90" : "rotate-0",
    )}
  >
    <path
      d="M6 3.5L10.5 8L6 12.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HamburgerGlyph = ({ color }: { color: string }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <path d="M4 8h20M4 14h20M4 20h20" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CloseGlyph = ({ color }: { color: string }) => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
    <path d="M7 7l14 14M21 7L7 21" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Widget                                                              */
/* ------------------------------------------------------------------ */

const NavBar1047 = ({ data }: { data: any }) => {
  const sectionData = data?.data ?? data;
  const isPreview = Boolean(data?.isPreview ?? false);
  const pathname = usePathname() || "";
  const dispatch = useDispatch();

  const navRef = useRef<HTMLDivElement>(null);
  /**
   * The MOBILE drawer, which is a SIBLING of `navRef` — `renderMobileMenu()` is
   * rendered straight into the <header>, while `navRef` is on the <nav> inside
   * the desktop block.
   *
   * The outside-click handler below unmounts the open submenu, and it runs on
   * `mousedown`. Testing against `navRef` alone made every tap in the drawer
   * count as "outside": the grandchild list was torn down between mousedown and
   * click, so the click never reached the <Link> and the items nested under a
   * branch did nothing at all. Their siblings survived because only the expanded
   * list is conditional on `expandedChild`. Desktop was unaffected — that markup
   * IS inside `navRef`.
   */
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  /** Third level: which dropdown row has its specialisations expanded. */
  const [expandedChild, setExpandedChild] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);

  const {
    logo_icon = "",
    logo_icon_alt = "Logo",
    mobile_logo_icon = "",
    mobile_logo_icon_alt = "",
    background_color = "#FFFFFF",
    nav_text_color = "#1B1B1B",
    active_menu_color = "#FF0013",
    active_menu_color_2 = "",
    /* v1's Tailwind `borderColor.DEFAULT` — it overrides gray.200 to #7C8493. */
    border_color = "#7C8493",
    mobile_menu_text_color = "#1B1B1B",
    dropdown_bg_color = "#FFFFFF",
    dropdown_text_color = "#1B1B1B",
    cta3_label = "Apply",
    cta3_href = "",
    cta3_bg_color = "#FF0013",
    cta3_bg_color_2 = "",
    cta3_text_color = "#FFFFFF",
    hamburger_icon = "",
    hamburger_icon_alt = "Menu",
    mobile_cancel_icon = "",
    mobile_cancel_icon_alt = "Close",
    menuItems = [],
  } = sectionData ?? {};

  const showDropdownIcons = toBool(sectionData?.show_dropdown_icons, false);
  /* Hamburger visibility. Defaults TRUE — a missing or NULL value must keep
     the hamburger, so only an explicit editor opt-out ever hides it. */
  const showHamburger = toBool(sectionData?.show_hamburger, true);
  const cta3Enabled = toBool(sectionData?.cta3_enabled, true);
  const stickyToTop = toBool(sectionData?.sticky_to_top, true);

  const routes: RouteItem[] = useMemo(() => toRoutes(menuItems), [menuItems]);

  /** v1 marks "/" active only on an exact match; everything else on prefix. */
  const isRouteActive = (route: string) => {
    if (!route) return false;
    if (route === "/") return pathname === "/";
    const normalized = route.startsWith("/") ? route : `/${route}`;
    return pathname === normalized || pathname.startsWith(`${normalized}/`);
  };

  // Close on outside click, and whenever the route changes (v1 resets on pathname).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // Inside EITHER the desktop nav or the mobile drawer is inside the navbar.
      const insideNav = navRef.current?.contains(target) ?? false;
      const insideMobile = mobileMenuRef.current?.contains(target) ?? false;
      if (!insideNav && !insideMobile) {
        setOpenDropdown(null);
        setExpandedChild(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
    setExpandedChild(null);
    setIsMobileMenuOpen(false);
    setOpenMobileAccordion(null);
  }, [pathname]);

  const applyHref = str(cta3_href);
  const activeAccent = accentBackground(
    str(active_menu_color) || "#FF0013",
    str(active_menu_color_2),
    ACTIVE_GRADIENT_ANGLE,
  );
  const isActiveGradient = activeAccent.startsWith("linear-gradient");

  const handleApplyClick = () => {
    pushGtmEvent("cta_click", {
      section: "navbar",
      label: cta3_label || "apply",
      link_url: applyHref,
    });
    if (applyHref) return;
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  /* -- shared pieces -------------------------------------------------- */

  const applyCta = (extraClass?: string) => {
    if (!cta3Enabled || !cta3_label) return null;
    const className = cn(
      "inline-flex h-7 shrink-0 items-center justify-center whitespace-nowrap rounded px-5 text-[16px] font-medium leading-[24px] shadow transition-opacity hover:opacity-90",
      extraClass,
    );
    const style = {
      background: accentBackground(
        str(cta3_bg_color) || "#FF0013",
        str(cta3_bg_color_2),
        "to right",
      ),
      color: cta3_text_color,
    };

    if (applyHref) {
      return (
        <a
          href={applyHref}
          onClick={handleApplyClick}
          data-cta-name="apply"
          target={isExternal(applyHref) ? "_blank" : undefined}
          rel={isExternal(applyHref) ? "noopener noreferrer" : undefined}
          className={className}
          style={style}
        >
          {cta3_label}
        </a>
      );
    }

    return (
      <button
        type="button"
        onClick={handleApplyClick}
        data-cta-name="apply"
        className={cn(className, "cursor-pointer")}
        style={style}
      >
        {cta3_label}
      </button>
    );
  };

  const logoEl = (sizeClass: string, wrapperClass: string, mobile = false) => {
    const src = mobile && mobile_logo_icon ? mobile_logo_icon : logo_icon;
    const alt =
      (mobile && mobile_logo_icon ? mobile_logo_icon_alt : logo_icon_alt) || "Logo";
    if (!src) return null;
    return (
      <Link href="/" className={wrapperClass} aria-label={alt}>
        <Image
          src={src}
          alt={alt}
          width={180}
          height={42}
          priority
          className={cn("object-contain object-left", sizeClass)}
          unoptimized={shouldUnoptimizeImageSrc(src)}
        />
      </Link>
    );
  };

  /** Top-level label — gradient text when active, matching v1's `bg-clip-text`. */
  const navLabelStyle = (route: RouteItem, active: boolean) =>
    active
      ? isActiveGradient
        ? {
            backgroundImage: activeAccent,
            WebkitBackgroundClip: "text" as const,
            backgroundClip: "text" as const,
            color: "transparent",
          }
        : { color: activeAccent }
      : { color: route.textColor || nav_text_color };

  /* -- desktop / tablet dropdown -------------------------------------- */

  const renderDropdown = (route: RouteItem) => {
    /**
     * v1 ships TWO visually distinct dropdowns and this one widget covers both.
     * The Programmes menu — the only one with a third level — overrides shadcn's
     * width to `min-w-[200px]` and lays its rows out as `p-0` + an inner
     * `flex-1 py-2 pl-4`, the 16px indent leaving the right edge free for the
     * expand chevron. Student Corner / Admission keep the 15rem default and
     * indent their rows `px-8`. A third level anywhere in the menu is what tells
     * the two apart, so it also decides how this menu's LEAF rows are indented —
     * a specialisation-less programme sits at pl-4 like its siblings, not px-8.
     */
    const isProgrammeStyle = route.children.some((child) => child.children.length > 0);

    return (
      <div
        className={cn(
          /* v1 centres the card under its trigger (Radix `align="center"`) and
             opens it 13px clear of the bar — `sideOffset={5}` plus the content's
             own `my-2`. Menus that take shadcn's default sideOffset of 4 sit 1px
             tighter than that; not worth a second value. */
          "animate-navbar-dropdown absolute left-1/2 top-full z-50 mt-[13px] -translate-x-1/2 overflow-hidden rounded-md border p-1 shadow-md",
          isProgrammeStyle ? "min-w-[200px]" : "min-w-[15rem]",
        )}
        style={{ backgroundColor: dropdown_bg_color, borderColor: border_color }}
      >
        {route.children.map((child) => {
          const hasGrandChildren = child.children.length > 0;
          const childActive = isRouteActive(child.route);

          return (
            <div key={child.id} className="w-full">
              {hasGrandChildren ? (
                <button
                  type="button"
                  onClick={() =>
                    setExpandedChild(expandedChild === child.id ? null : child.id)
                  }
                  className="flex w-full cursor-pointer items-center justify-between rounded-sm pr-2 text-[12px]"
                  style={{
                    color: child.textColor || dropdown_text_color,
                    backgroundColor: childActive ? HIGHLIGHT_BG : "transparent",
                  }}
                  aria-expanded={expandedChild === child.id}
                >
                  <span className="flex-1 py-2 pl-4 text-left">{child.label}</span>
                  <ChevronRight
                    open={expandedChild === child.id}
                    color={child.textColor || dropdown_text_color}
                  />
                </button>
              ) : (
                <Link
                  href={child.route}
                  target={isExternal(child.route) ? "_blank" : undefined}
                  rel={isExternal(child.route) ? "noopener noreferrer" : undefined}
                  onClick={() => setOpenDropdown(null)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2 rounded-sm py-2 text-[12px]",
                    isProgrammeStyle ? "pl-4 pr-2" : "px-8",
                  )}
                  style={{
                    color: child.textColor || dropdown_text_color,
                    backgroundColor: childActive ? HIGHLIGHT_BG : "transparent",
                  }}
                >
                  {showDropdownIcons && child.icon ? (
                    <Image
                      src={child.icon}
                      alt={child.iconAlt || ""}
                      width={16}
                      height={16}
                      className="shrink-0 object-contain"
                      unoptimized={shouldUnoptimizeImageSrc(child.icon)}
                    />
                  ) : null}
                  {child.label}
                </Link>
              )}

              {hasGrandChildren && expandedChild === child.id ? (
                <div
                  className="flex w-full flex-col items-start space-y-2 border-y px-0 py-1 transition-all duration-300"
                  style={{
                    backgroundColor: dropdown_bg_color,
                    borderColor: border_color,
                  }}
                >
                  {child.children.map((grandChild) => (
                    <Link
                      key={grandChild.id}
                      href={grandChild.route}
                      onClick={() => {
                        setExpandedChild(null);
                        setOpenDropdown(null);
                      }}
                      className="w-full cursor-pointer px-5 py-1 text-[12px]"
                      style={{
                        color: grandChild.textColor || SUBITEM_TEXT,
                        backgroundColor: isRouteActive(grandChild.route)
                          ? HIGHLIGHT_BG
                          : "transparent",
                      }}
                    >
                      {grandChild.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  /* -- mobile drawer -------------------------------------------------- */

  const renderMobileMenu = () => (
    /**
     * NOT a full-width drawer. v1's hamburger menu is the same shadcn
     * `DropdownMenuContent` as the desktop dropdowns — so it is a 240px
     * (`min-w-[15rem]`) rounded, bordered card, 12px below the bar
     * (sideOffset 4 + `my-2`), sitting flush against the viewport's left edge:
     * Radix centres it under the hamburger, then collision-shifts it back
     * inside. `left-0` on the header reproduces that landing spot directly.
     *
     * The width is a MIN, not a fixed size — an absolutely positioned box is
     * shrink-to-fit, which is exactly how Radix sizes it, so a long label grows
     * the card here the same way it does on bduedu.in.
     */
    <div
      ref={mobileMenuRef}
      className="animate-navbar-dropdown absolute left-0 top-full z-[100] mt-3 flex max-h-[80vh] min-w-[15rem] flex-col overflow-y-auto rounded-md border p-1 shadow-md min-[860px]:hidden"
      style={{ backgroundColor: dropdown_bg_color, borderColor: border_color }}
    >
      {routes.map((route) => {
        const active = isRouteActive(route.route);

        if (route.children.length === 0) {
          return (
            <Link
              key={route.id}
              href={route.route}
              onClick={() => setIsMobileMenuOpen(false)}
              className="cursor-pointer rounded-sm px-3 py-3 text-[12px] font-bold"
              style={navLabelStyle(route, active)}
            >
              {route.label}
            </Link>
          );
        }

        return (
          <div
            key={route.id}
            className="w-full border-b"
            style={{ borderColor: ACCORDION_DIVIDER }}
          >
            <button
              type="button"
              onClick={() =>
                setOpenMobileAccordion(
                  openMobileAccordion === route.id ? null : route.id,
                )
              }
              className="flex w-full cursor-pointer items-center justify-between rounded-sm p-3 text-[13px]"
              style={{ color: mobile_menu_text_color }}
              aria-expanded={openMobileAccordion === route.id}
            >
              {route.label}
              <ChevronRight
                open={openMobileAccordion === route.id}
                color={mobile_menu_text_color}
              />
            </button>

            {openMobileAccordion === route.id ? (
              <div className="flex flex-col">
                {route.children.map((child) => {
                  const hasGrandChildren = child.children.length > 0;

                  return (
                    <div key={child.id} className="w-full">
                      {hasGrandChildren ? (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedChild(
                              expandedChild === child.id ? null : child.id,
                            )
                          }
                          className="flex w-full cursor-pointer items-center justify-between rounded-sm pr-3 text-[12px]"
                          style={{ color: child.textColor || dropdown_text_color }}
                          aria-expanded={expandedChild === child.id}
                        >
                          <span className="flex-1 py-2 pl-4 pr-2 text-left">
                            {child.label}
                          </span>
                          <ChevronRight
                            open={expandedChild === child.id}
                            color={child.textColor || dropdown_text_color}
                          />
                        </button>
                      ) : (
                        <Link
                          href={child.route}
                          target={isExternal(child.route) ? "_blank" : undefined}
                          rel={
                            isExternal(child.route) ? "noopener noreferrer" : undefined
                          }
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block cursor-pointer rounded-sm px-8 py-2 text-[12px]"
                          style={{ color: child.textColor || dropdown_text_color }}
                        >
                          {child.label}
                        </Link>
                      )}

                      {hasGrandChildren && expandedChild === child.id ? (
                        <div
                          className="flex w-full flex-col items-start space-y-2 border-y px-0 py-1"
                          style={{ borderColor: border_color }}
                        >
                          {child.children.map((grandChild) => (
                            <Link
                              key={grandChild.id}
                              href={grandChild.route}
                              onClick={() => {
                                setExpandedChild(null);
                                setIsMobileMenuOpen(false);
                              }}
                              className="w-full cursor-pointer px-5 py-1 text-[12px]"
                              style={{ color: grandChild.textColor || SUBITEM_TEXT }}
                            >
                              {grandChild.label}
                            </Link>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  return (
    <header
      className={cn(
        "w-full",
        /* `relative` and `sticky` must not both apply — the last Tailwind
           utility wins and breaks sticky.
           z-[90] is the sticky-nav layer (NavBar 4 uses the same): above Promo
           Pill's z-[85], below the z-[100] contact strip. Sticky + z-index makes
           this a stacking context, so the dropdowns' own z-50/z-[100] only order
           them WITHIN the nav — the bar's own value is what clears the pill. */
        isPreview || !stickyToTop ? "relative" : "sticky z-[90]",
      )}
      style={{
        backgroundColor: background_color,
        /* Sits below a pinned contact strip, reproducing v1's `top-[42px]`.
           Header 1046 / 5 / 7 publish their height into this property. */
        ...(isPreview || !stickyToTop
          ? {}
          : { top: "var(--header4-sticky-height, 0px)" }),
      }}
    >
      {/* ---------- Desktop + tablet bar (≥ 860px) ---------- */}
      <div className="mx-auto hidden h-[64px] max-w-[2024px] min-[860px]:block min-[1024px]:h-[65px]">
        <div className="flex h-full items-center justify-between">
          {logoEl(
            "h-[32px] w-[120px] min-[1024px]:h-[42px] min-[1024px]:w-[180px]",
            "flex shrink-0 cursor-pointer items-center pl-3 min-[1024px]:pl-5",
          )}

          <div className="flex gap-3">
            {/*
              NO gap utility here, however much v1's `lg:space-x-5` suggests one.
              `space-x-*` works by putting `margin-left` on the siblings, and every
              one of v1's nav items also carries `!m-0` — `margin:0!important`,
              which outranks it. So the 20px never lands on bduedu.in and the only
              thing separating the labels is each item's own px-3.

              Porting `space-x-5` as `gap-5` silently REVIVED that dead 20px,
              because `gap` is its own property and no margin rule can cancel it —
              44px between labels instead of prod's 24px. Match the rendering, not
              the source.
            */}
            <nav ref={navRef} className="flex h-full items-center">
              {routes.map((route) => {
                const active = isRouteActive(route.route);

                if (route.children.length === 0) {
                  return (
                    <Link
                      key={route.id}
                      href={route.route}
                      target={isExternal(route.route) ? "_blank" : undefined}
                      rel={
                        isExternal(route.route) ? "noopener noreferrer" : undefined
                      }
                      className="cursor-pointer rounded-sm px-2 py-1 min-[1024px]:px-3"
                    >
                      <p
                        className="text-[13px] font-semibold"
                        style={navLabelStyle(route, active)}
                      >
                        {route.label}
                      </p>
                    </Link>
                  );
                }

                const open = openDropdown === route.id;

                return (
                  <div key={route.id} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setOpenDropdown(open ? null : route.id);
                        setExpandedChild(null);
                      }}
                      className="cursor-pointer rounded-sm px-2 py-1 min-[1024px]:px-3"
                      style={{ backgroundColor: open ? HIGHLIGHT_BG : "transparent" }}
                      aria-expanded={open}
                    >
                      <span
                        className="flex items-center justify-between text-[13px] font-semibold"
                        style={navLabelStyle(route, active)}
                      >
                        {route.label}
                        <ChevronDown
                          open={open}
                          color={
                            active && !isActiveGradient
                              ? activeAccent
                              : route.textColor || nav_text_color
                          }
                        />
                      </span>
                    </button>
                    {open ? renderDropdown(route) : null}
                  </div>
                );
              })}
            </nav>

            <div className="flex items-center justify-end gap-[10px] pr-3 min-[1024px]:pr-10">
              {applyCta()}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Mobile bar (< 860px) ---------- */}
      <div className="flex h-16 w-full items-center justify-between min-[860px]:hidden">
        <div className="flex items-center space-x-3 px-3">
          {routes.length > 0 && showHamburger ? (
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="flex shrink-0 items-center justify-center"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                mobile_cancel_icon ? (
                  <Image
                    src={mobile_cancel_icon}
                    alt={mobile_cancel_icon_alt}
                    width={28}
                    height={28}
                    className="object-contain"
                    unoptimized={shouldUnoptimizeImageSrc(mobile_cancel_icon)}
                  />
                ) : (
                  <CloseGlyph color={nav_text_color} />
                )
              ) : hamburger_icon ? (
                <Image
                  src={hamburger_icon}
                  alt={hamburger_icon_alt}
                  width={28}
                  height={28}
                  className="object-contain"
                  unoptimized={shouldUnoptimizeImageSrc(hamburger_icon)}
                />
              ) : (
                <HamburgerGlyph color={nav_text_color} />
              )}
            </button>
          ) : null}

          {logoEl("h-[38px] w-[140px]", "flex cursor-pointer items-center", true)}
        </div>

        <div className="flex items-center justify-end space-x-3 px-3">
          {applyCta()}
        </div>
      </div>

      {isMobileMenuOpen ? renderMobileMenu() : null}
    </header>
  );
};

export default NavBar1047;
